import type { LoginMethod } from "@/context";
import { Action } from "@/context/AnalyticsProvider";
import type { GamePageEvent } from "@/context/AnalyticsProvider/gamePage";
import { GamePageSection } from "@/context/AnalyticsProvider/gamePage";
import { inFacebookBrowser, isTikTokBrowser } from "@/utils/browser";
import { redirectGameToExternalBrowser } from "@/utils/loginredirect";
import { notifyError } from "@/utils/monitoring";
import { z } from "zod";

export interface GPOnboardingStepProps {
  onNext: () => void;
  isVisible: boolean;
  track: (event: GamePageEvent) => void;
}

export enum GPOnboardingStep {
  LOGIN = "login",
  BRONZE_REWARD_ACHIEVED = "bronze-reward-achieved",
  LINK_STEAM = "link-steam",
  SILVER_REWARD_ACHIEVED = "silver-reward-achieved",
  WISHLIST_STEAM = "wishlist-steam",
  WISHLIST_STEAM_VERIFY = "wishlist-steam-verify",
  FOLLOW_ON_X = "follow-on-x",
  GOLD_REWARD_ACHIEVED = "gold-reward-achieved",
}

// Set by ?testId= query param
export enum GPExperimentType {
  EXTRINSIC = "ExtrinsicRewards",
  HYBRID = "HybridRewards",
  INGAME = "InGameRewards",
  LINK_TREE = "ExtrinsicLinktreeRewards",
  // experimentType.includes("NoPrompt") experiments will not auto open modal on page load
  NO_PROMPT = "NoPrompt",
  NO_PROMPT_EXTRINSIC = "NoPromptExtrinsic",
  NO_PROMPT_INGAME = "NoPromptInGame",
}

// For backward compatibility with utm_content query param
export enum GPOnboardingTypeUTM {
  EXTRINSIC = "25-11-27_ExtrinsicRewards_Acq-Broad_8-Ball_Leads_All-Placements",
  HYBRID = "25-11-27_HybridRewards_Acq-Broad_8-Ball_Leads_All-Placements",
  INGAME = "25-11-27_In-GameRewards_Acq-Broad_8-Ball_Leads_All-Placements",
  NO_PROMPT = "25-11-27_NoPrompt_Acq-Broad_8-Ball_Leads_All-Placements",
}
// Map UTM values to standard experiment values
const utmTypeMap: Record<GPOnboardingTypeUTM, GPExperimentType> = {
  [GPOnboardingTypeUTM.EXTRINSIC]: GPExperimentType.EXTRINSIC,
  [GPOnboardingTypeUTM.HYBRID]: GPExperimentType.HYBRID,
  [GPOnboardingTypeUTM.INGAME]: GPExperimentType.INGAME,
  [GPOnboardingTypeUTM.NO_PROMPT]: GPExperimentType.NO_PROMPT,
};

/**
 * Gets the experiment type from search params, with legacy support for utm_content
 */
export function getExperimentTypeFromSearchParams(
  searchParams: URLSearchParams | null,
): GPExperimentType | undefined {
  if (!searchParams) return undefined;

  // Prefer testId query param
  const testId = searchParams.get("testId");
  if (testId) {
    const type = Object.values(GPExperimentType).find(
      (value) => value === testId,
    );
    if (type) return type;
  }

  // Fallback to utm_content
  const utmContent = searchParams.get("utm_content");
  if (utmContent) {
    const utmType = Object.values(GPOnboardingTypeUTM).find(
      (value) => value === utmContent,
    ) as GPOnboardingTypeUTM | undefined;
    if (utmType) {
      return utmTypeMap[utmType];
    }
  }

  return undefined;
}

// Common login constants
export const SOCIAL_LOGINS: LoginMethod[] = ["google", "apple", "facebook"];

// Type for login handler dependencies
export interface UseLoginHandlerDeps {
  loginDirectly: (method: LoginMethod) => Promise<unknown>;
  track: (event: GamePageEvent) => void;
  setDisableLoadingModal: (disable: boolean) => void;
  isLoggedIn?: boolean;
}

// Common login handler logic
export async function handleLoginLogic(
  loginMethod: LoginMethod,
  deps: UseLoginHandlerDeps,
): Promise<void> {
  const { loginDirectly, track, setDisableLoadingModal, isLoggedIn } = deps;

  if (isLoggedIn) return;

  // Redirect to external browser if in fb browser or tiktok browser
  if (inFacebookBrowser() || isTikTokBrowser()) {
    const redirected = redirectGameToExternalBrowser(false, undefined, {
      loginMethod:
        typeof loginMethod === "string"
          ? loginMethod
          : JSON.stringify(loginMethod),
      loginRedirect: "true",
    });
    if (redirected) return;
  }

  setDisableLoadingModal(true);
  const isSocialLogin = typeof loginMethod === "string";
  const control = (
    isSocialLogin ? `social-login-${loginMethod}` : "email-login"
  ) as Lowercase<string>;

  try {
    await loginDirectly(loginMethod);
    track({
      action: Action.SUCCEEDED,
      extras: {
        control,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
  } catch (error) {
    track({
      action: Action.FAILED,
      extras: {
        control,
        section: GamePageSection.ONBOARDING_MODAL,
      },
    });
    notifyError(error, "login");
  } finally {
    setDisableLoadingModal(false);
  }
}

// Email validation helper
export function validateEmail(email: string): string | null {
  if (!email.trim()) {
    return "Email is required";
  }
  if (z.string().email().safeParse(email).error) {
    return "Invalid email address";
  }
  return null;
}
